import Button from 'components/Form/Button';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useCallback, useEffect, useState } from 'react';
import BankBeneficiaries from './Assets/BankBeneficiaries';
import CreateInstructionModal from './CreateInstructionModal';
import { Link } from 'react-router-dom';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { selectAccountDetails } from 'selectors/account-selector';
import { useAppSelector } from 'store/hooks';
import { createMarkup } from 'utils';
import RSABeneficiaries from './Assets/RSABeneficiaries';
import StocksBeneficiaries from './Assets/StocksBeneficiaries';
import EmployeeBeneficiaries from './Assets/EmployeeBeneficiaries';
import PhysicalBeneficiaries from './Assets/PhysicalBeneficiaries';
import BondBeneficiaries from './Assets/BondBeneficiaries';
import withCreatePortal from 'components/HOC/withCreatePortal';
import Badge from 'components/Badge';
import {
  useDownloadWill,
  useSendWillForApproval
} from 'common/queries-and-mutations/user';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import ConfirmModal from 'components/Modal/ConfirmModal';
import WillPreview from './WillPreview';

const EhancedCreateInstructionModal = withCreatePortal(CreateInstructionModal);
const EnhancedConfirmModal = withCreatePortal(ConfirmModal);
const EnhancedWillPreviewModal = withCreatePortal(WillPreview);

export default function WillSummary() {
  const [showInstructionNote, setShowInstructionNote] = useState(false);
  const { user } = useAppSelector(selectAccountDetails);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAskToConfirm, setShowAskToConfirm] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const { mutate, isLoading } = useDownloadWill();
  const {
    mutate: sendForApproval,
    isSuccess,
    isError
  } = useSendWillForApproval();

  const handleSendForApproval = () => {
    setIsSubmitting(true);
    sendForApproval();
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      setShowAskToConfirm(false);
      setShowPreviewModal(false);
      toast(
        <Alert message="Will Successfully Sent to Lawyer" type="success" />
      );
    } else {
      setIsSubmitting(false);
      setShowAskToConfirm(false);
    }
  }, [isSuccess, isError]);

  const determineButton = useCallback(() => {
    if (user.isAccountLocked && !user.sentForApproval) {
      return (
        <Button
          variant="primary"
          size="md"
          className="mt-1 w-[12rem] rounded border-none px-[0.5rem] text-sm font-bold"
          label="Download Will For Signing"
          type="button"
          disabled={isLoading}
          loading={isLoading}
          onClick={() => mutate({ user })}
        ></Button>
      );
    } else if (user.sentForApproval) {
      return (
        <div className="w-full">
          <Badge
            className="w-full"
            title={'Awaiting Lawyer Approval'}
            type="warning"
            size="lg"
          />
        </div>
      );
    } else {
      return (
        <Button
          variant="primary"
          size="md"
          className="mt-1 w-[12rem] rounded border-none px-[0.5rem] text-sm font-bold"
          label="Send To Lawyer"
          loading={isSubmitting}
          disabled={isSubmitting}
          type="button"
          // onClick={() => setShowAskToConfirm(true)}
          onClick={() => setShowPreviewModal(true)}
        ></Button>
      );
    }
  }, [isLoading, isSubmitting, mutate, user]);

  return (
    <div>
      <div className="grid grid-cols-3">
        <div className="col-span-2 col-start-1">
          <Breadcrumb title="Will Summary" />
        </div>
        <div className="col-span-1 col-start-7 col-end-7 flex items-center gap-5">
          {(!user?.instructionDescription || !user?.instructionTitle) && (
            <Button
              variant="outline"
              size="md"
              className="mt-1 w-[12rem] rounded border border-[#FF6600] bg-[#FF66001A] px-[0.5rem] text-sm font-bold text-[#FF6600]"
              label="Special Instructions"
              onClick={() => setShowInstructionNote(true)}
            ></Button>
          )}
          {determineButton()}
        </div>
      </div>

      <div className="mt-2 flex flex-col space-y-3 overflow-y-auto px-[var(--margin-x)] transition-all duration-[.25s] scrollbar-hide md:h-[800px]">
        <BankBeneficiaries />
        <RSABeneficiaries />
        <StocksBeneficiaries />
        <EmployeeBeneficiaries />
        <PhysicalBeneficiaries />
        <BondBeneficiaries />
        {(user.instructionDescription || user.instructionTitle) && (
          <div className="mt-2 flex flex-col space-y-4">
            <div className="bg-white">
              <div className="border-b border-[#E5E5E5]">
                <p className="py-6 pl-10 font-bold text-[#34475E]">
                  {user.instructionTitle}
                </p>
              </div>
              <div className="px-14 py-8">
                <p
                  dangerouslySetInnerHTML={createMarkup(
                    user.instructionDescription
                  )}
                  className="text-[12px] font-[500] text-[#434343]"
                ></p>
                <div className="flex items-center justify-end gap-x-2">
                  <Link to="preview">
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-1 w-[12rem] rounded border border-[#FF6600] bg-[#FF66001A] px-[0.5rem] text-sm font-bold text-[#FF6600]"
                      label="View"
                    ></Button>
                  </Link>
                  <Link to="update-special-instructions">
                    <Button
                      variant="primary"
                      size="sm"
                      className="mt-1 w-[12rem] rounded border-none px-[0.5rem] text-sm font-bold"
                      label="Edit"
                    ></Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
        {showInstructionNote && <EhancedCreateInstructionModal />}
      </div>

      {showAskToConfirm && (
        <EnhancedConfirmModal
          content="Are you sure you want to send this to the lawyer for review? Note that once sent, you won't be able to add, edit, or delete any records."
          onConfirm={() => {
            setShowAskToConfirm(false);
            handleSendForApproval();
          }}
          onCancel={() => {
            setShowAskToConfirm(false);
          }}
          actionText="Yes"
          cancelText="No"
          title="Action Required"
        />
      )}

      {showPreviewModal && (
        <EnhancedWillPreviewModal
          name={`${user.othername} ${user.surname}'s Will`}
          onSend={() => {
            setShowAskToConfirm(true);
          }}
          onCancel={() => {
            setShowPreviewModal(false);
          }}
          isSubmitting={isLoading || isSubmitting}
        />
      )}
    </div>
  );
}
